/**
 * Resolve the set of edges that a Bevel modifier should bevel.
 *
 * The bevel modifier supports three limit methods (mirroring Blender):
 *   - "none":        every bevelable interior edge
 *   - "angle":       edges whose dihedral angle exceeds `angleLimit`
 *   - "vertexGroup": bevelable edges whose both endpoints are in the resolved group
 *
 * This runs in the modifier worker on every pipeline evaluation, so it is
 * deliberately allocation-light and avoids a second WASM round-trip.
 */

import type { BlendBevelModifier } from "../../blend-reader/types/BlendModifier.ts";
import { edgeFaces, getAdjacencyCache } from "../../modeling/topology.ts";
import {
  EDGE_BOUNDARY,
  EDGE_WIRE,
  type PolygonTopology,
} from "../../modeling/types.ts";

const EDGE_EXCLUDE = EDGE_WIRE | EDGE_BOUNDARY;
const COPLANAR_FACE_DOT_EPSILON = 1e-5;

export type BevelEdgeDiagnosticReason =
  | "selected"
  | "wire"
  | "boundary"
  | "nonManifold"
  | "coplanar"
  | "vertexGroup"
  | "angleLimit";

export interface BevelEdgeDiagnostic {
  edgeIndex: number;
  edge: [number, number];
  faces: Uint32Array;
  selected: boolean;
  reason: BevelEdgeDiagnosticReason;
  faceDot: number | null;
  faceAngleDegrees: number | null;
  profileSign: number | null;
  profileKind: "convex" | "concave" | "unknown";
}

/**
 * Accumulate Newell's method face normal for face `f` into a scratch vec3.
 * Output may have arbitrary magnitude — caller normalizes before dot product.
 */
function accumulateFaceNormal(
  topology: PolygonTopology,
  positions: Float32Array,
  f: number,
  out: [number, number, number],
): void {
  const start = topology.offsets[f];
  const end = topology.offsets[f + 1];
  const size = end - start;
  let nx = 0,
    ny = 0,
    nz = 0;
  for (let j = 0; j < size; j++) {
    const a = topology.corners[start + j];
    const b = topology.corners[start + ((j + 1) % size)];
    const ax = positions[a * 3],
      ay = positions[a * 3 + 1],
      az = positions[a * 3 + 2];
    const bx = positions[b * 3],
      by = positions[b * 3 + 1],
      bz = positions[b * 3 + 2];
    nx += (ay - by) * (az + bz);
    ny += (az - bz) * (ax + bx);
    nz += (ax - bx) * (ay + by);
  }
  out[0] = nx;
  out[1] = ny;
  out[2] = nz;
}

function buildFaceNormals(
  topology: PolygonTopology,
  positions: Float32Array,
): Float32Array {
  const faceCount = topology.offsets.length - 1;
  const faceNormals = new Float32Array(faceCount * 3);
  const scratch: [number, number, number] = [0, 0, 0];
  for (let f = 0; f < faceCount; f++) {
    accumulateFaceNormal(topology, positions, f, scratch);
    faceNormals[f * 3] = scratch[0];
    faceNormals[f * 3 + 1] = scratch[1];
    faceNormals[f * 3 + 2] = scratch[2];
  }
  return faceNormals;
}

function edgeFaceCosine(
  adj: ReturnType<typeof getAdjacencyCache>,
  ei: number,
  faceNormals: Float32Array,
): number | null {
  const faces = edgeFaces(adj, ei);
  if (faces.length !== 2) {
    return faces.length > 2 ? -1 : null;
  }
  const f0 = faces[0];
  const f1 = faces[1];
  const ax = faceNormals[f0 * 3];
  const ay = faceNormals[f0 * 3 + 1];
  const az = faceNormals[f0 * 3 + 2];
  const bx = faceNormals[f1 * 3];
  const by = faceNormals[f1 * 3 + 1];
  const bz = faceNormals[f1 * 3 + 2];
  const la = Math.hypot(ax, ay, az);
  const lb = Math.hypot(bx, by, bz);
  if (la === 0 || lb === 0) return null;
  return (ax * bx + ay * by + az * bz) / (la * lb);
}

/**
 * Drop coplanar (180°-dihedral) edges that have NO beveled neighbour
 * sharing a vertex with them. Coplanar edges adjacent to non-coplanar
 * beveled edges stay in the set — the kernel needs them to participate
 * in corner-cap topology so the cap polygon at a mixed-dihedral vertex
 * has one BoundVert per incident edge (matching Blender's offset_meet
 * construction). Stage-2 picks up the linear-chord fallback for the
 * resulting flat strip; visually the bevel is invisible on the
 * coplanar edge, but the surrounding corner caps round cleanly.
 *
 * Pure-coplanar islands (an interior edge surrounded by other coplanar
 * edges with no nearby bevel) stay filtered because beveling them
 * just produces stray flat ribbons with no corner attachment.
 */
function filterCoplanarInteriorEdges(
  positions: Float32Array,
  topology: PolygonTopology,
  candidates: Set<number>,
): Set<number> {
  if (candidates.size === 0) return candidates;
  const adj = getAdjacencyCache(topology, positions.length / 3);
  const faceNormals = buildFaceNormals(topology, positions);

  // Classify each candidate edge as coplanar or not, and remember which
  // vertices touch a non-coplanar beveled edge.
  const coplanar = new Set<number>();
  const verticesOnNonCoplanarBev = new Set<number>();
  for (const ei of candidates) {
    const cos = edgeFaceCosine(adj, ei, faceNormals);
    if (cos == null) continue;
    if (cos >= 1 - COPLANAR_FACE_DOT_EPSILON) {
      coplanar.add(ei);
    } else {
      verticesOnNonCoplanarBev.add(topology.edges[ei * 2]);
      verticesOnNonCoplanarBev.add(topology.edges[ei * 2 + 1]);
    }
  }

  const out = new Set<number>();
  for (const ei of candidates) {
    const cos = edgeFaceCosine(adj, ei, faceNormals);
    if (cos == null) continue;
    if (!coplanar.has(ei)) {
      out.add(ei);
      continue;
    }
    const v0 = topology.edges[ei * 2];
    const v1 = topology.edges[ei * 2 + 1];
    if (verticesOnNonCoplanarBev.has(v0) || verticesOnNonCoplanarBev.has(v1)) {
      out.add(ei);
    }
  }
  return out;
}

function computeProfileSign(
  topology: PolygonTopology,
  positions: Float32Array,
  edgeIndex: number,
  faceNormals: Float32Array,
  faces: Uint32Array,
): number | null {
  if (faces.length !== 2) return null;
  const v0 = topology.edges[edgeIndex * 2];
  const v1 = topology.edges[edgeIndex * 2 + 1];
  const ex = positions[v1 * 3] - positions[v0 * 3];
  const ey = positions[v1 * 3 + 1] - positions[v0 * 3 + 1];
  const ez = positions[v1 * 3 + 2] - positions[v0 * 3 + 2];
  const el = Math.hypot(ex, ey, ez);
  if (el <= 1e-8) return null;

  const f0 = faces[0];
  const f1 = faces[1];
  const ax = faceNormals[f0 * 3];
  const ay = faceNormals[f0 * 3 + 1];
  const az = faceNormals[f0 * 3 + 2];
  const bx = faceNormals[f1 * 3];
  const by = faceNormals[f1 * 3 + 1];
  const bz = faceNormals[f1 * 3 + 2];

  const cx = ay * bz - az * by;
  const cy = az * bx - ax * bz;
  const cz = ax * by - ay * bx;
  return (cx * ex + cy * ey + cz * ez) / el;
}

function profileKindFromSign(
  profileSign: number | null,
): "convex" | "concave" | "unknown" {
  if (profileSign == null || Math.abs(profileSign) <= 1e-6) return "unknown";
  return profileSign > 0 ? "convex" : "concave";
}

export function resolveBevelEdgeSet(
  positions: Float32Array,
  topology: PolygonTopology,
  mod: BlendBevelModifier,
  vgAllowed: Set<number> | null,
): Set<number> {
  const out = new Set<number>();
  const edgeCount = topology.edges.length / 2;
  const flags = topology.edgeFlags;

  if (mod.limitMethod === "vertexGroup") {
    if (!vgAllowed || vgAllowed.size === 0) return out;
    for (let ei = 0; ei < edgeCount; ei++) {
      if (flags[ei] & EDGE_EXCLUDE) continue;
      const v0 = topology.edges[ei * 2];
      const v1 = topology.edges[ei * 2 + 1];
      if (vgAllowed.has(v0) && vgAllowed.has(v1)) out.add(ei);
    }
    return filterCoplanarInteriorEdges(positions, topology, out);
  }

  if (mod.limitMethod === "angle") {
    const vertexCount = positions.length / 3;
    const adj = getAdjacencyCache(topology, vertexCount);
    const faceNormals = buildFaceNormals(topology, positions);

    const cosThreshold = Math.cos((mod.angleLimit * Math.PI) / 180);

    for (let ei = 0; ei < edgeCount; ei++) {
      if (flags[ei] & EDGE_EXCLUDE) continue;
      const cos = edgeFaceCosine(adj, ei, faceNormals);
      if (cos == null) continue;
      // Include edges whose dihedral (acos of normal dot) is >= angleLimit.
      // cos is monotonic-decreasing in angle, so compare with <=.
      if (cos < 1 - COPLANAR_FACE_DOT_EPSILON && cos <= cosThreshold)
        out.add(ei);
    }
    return out;
  }

  // "none" — every bevelable interior (non-wire, non-boundary, non-coplanar) edge.
  for (let ei = 0; ei < edgeCount; ei++) {
    if (flags[ei] & EDGE_EXCLUDE) continue;
    out.add(ei);
  }
  return filterCoplanarInteriorEdges(positions, topology, out);
}

export function diagnoseBevelEdges(
  positions: Float32Array,
  topology: PolygonTopology,
  mod: BlendBevelModifier,
  vgAllowed: Set<number> | null,
): BevelEdgeDiagnostic[] {
  const edgeCount = topology.edges.length / 2;
  const flags = topology.edgeFlags;
  const adj = getAdjacencyCache(topology, positions.length / 3);
  const faceNormals = buildFaceNormals(topology, positions);
  const cosThreshold = Math.cos((mod.angleLimit * Math.PI) / 180);
  const diagnostics: BevelEdgeDiagnostic[] = [];

  for (let ei = 0; ei < edgeCount; ei++) {
    const v0 = topology.edges[ei * 2];
    const v1 = topology.edges[ei * 2 + 1];
    const faces = edgeFaces(adj, ei);
    const faceDot = edgeFaceCosine(adj, ei, faceNormals);
    const faceAngleDegrees =
      faceDot == null
        ? null
        : (Math.acos(Math.max(-1, Math.min(1, faceDot))) * 180) / Math.PI;
    const profileSign = computeProfileSign(
      topology,
      positions,
      ei,
      faceNormals,
      faces,
    );
    const profileKind = profileKindFromSign(profileSign);

    let selected = false;
    let reason: BevelEdgeDiagnosticReason;
    if (flags[ei] & EDGE_WIRE) {
      reason = "wire";
    } else if (flags[ei] & EDGE_BOUNDARY) {
      reason = "boundary";
    } else if (faces.length !== 2 || faceDot == null) {
      reason = "nonManifold";
    } else if (faceDot >= 1 - COPLANAR_FACE_DOT_EPSILON) {
      reason = "coplanar";
    } else if (mod.limitMethod === "vertexGroup") {
      selected = !!vgAllowed && vgAllowed.has(v0) && vgAllowed.has(v1);
      reason = selected ? "selected" : "vertexGroup";
    } else if (mod.limitMethod === "angle") {
      selected = faceDot <= cosThreshold;
      reason = selected ? "selected" : "angleLimit";
    } else {
      selected = true;
      reason = "selected";
    }

    diagnostics.push({
      edgeIndex: ei,
      edge: [v0, v1],
      faces,
      selected,
      reason,
      faceDot,
      faceAngleDegrees,
      profileSign,
      profileKind,
    });
  }

  return diagnostics;
}
